/**
 * Shim for @vertex-protocol/contracts
 *
 * The original @vertex-protocol/contracts package exposed ABIs, types, and
 * utility functions. In the Nado fork this was split into @nadohq/shared.
 *
 * This shim re-exports relevant symbols from @nadohq/shared plus stubs for
 * anything that is missing but still imported by the frontend.
 */
/* eslint-disable @typescript-eslint/no-require-imports */
const BigNumber = require('bignumber.js').default || require('bignumber.js');
const nadoShared = require('@nadohq/shared');

// parseRawExpirationTimestamp: parses a Vertex expiration bitmask.
// Bit layout (from original Vertex SDK):
//   bits 62-63: order type (0=default/gtc, 1=ioc, 2=fok, 3=post-only)
//   bit 61: reduce-only flag
//   bits 0-60: recv timestamp
function parseRawExpirationTimestamp(rawExpiration) {
  const val = BigInt(rawExpiration);
  const orderTypeCode = Number((val >> 62n) & 3n);
  const reduceOnly = Boolean((val >> 61n) & 1n);
  const recvTime = Number(val & ((1n << 61n) - 1n));
  const typeMap = ['default', 'ioc', 'fok', 'post_only'];
  return {
    type: typeMap[orderTypeCode] || 'default',
    reduceOnly,
    recvTime,
  };
}

// getExpirationTimestamp: encodes order type + reduce-only + time into a bitmask.
// This is the inverse of parseRawExpirationTimestamp above.
// Bit layout: bits 62-63 = order type, bit 61 = reduce-only, bits 0-60 = timestamp
const OrderExpirationType = {
  DEFAULT: 'default',
  IOC: 'ioc',
  FOK: 'fok',
  POST_ONLY: 'post_only',
};

function getExpirationTimestamp({ type, expirationTime, reduceOnly }) {
  const typeMap = { default: 0n, ioc: 1n, fok: 2n, post_only: 3n };
  const orderTypeCode = typeMap[type] ?? 0n;
  const reduceOnlyBit = reduceOnly ? 1n : 0n;
  const timestamp = BigInt(Math.floor(Number(expirationTime ?? 0)));

  const expiration =
    (orderTypeCode << 62n) | (reduceOnlyBit << 61n) | (timestamp & ((1n << 61n) - 1n));

  return expiration.toString();
}

// getTriggerOrderNonce: generates a nonce for trigger orders
function getTriggerOrderNonce() {
  // Trigger order nonces use the current timestamp in microseconds
  // Return as string to avoid BigInt serialization errors in JSON.stringify
  return (BigInt(Date.now()) * 1000n).toString();
}

// calcLpTokenValue: stub (LP not used in Stackd Perps)
function calcLpTokenValue(_lpTokens, _market) {
  return new BigNumber(0);
}

module.exports = {
  // Re-export from @nadohq/shared
  ProductEngineType: nadoShared.ProductEngineType,
  QUOTE_PRODUCT_ID: nadoShared.QUOTE_PRODUCT_ID,
  getDefaultRecvTime: nadoShared.getDefaultRecvTime,
  getOrderNonce: nadoShared.getOrderNonce,
  subaccountToHex: nadoShared.subaccountToHex,
  subaccountFromHex: nadoShared.subaccountFromHex,
  subaccountToBytes32: nadoShared.subaccountToBytes32,
  subaccountFromBytes32: nadoShared.subaccountFromBytes32,
  subaccountNameToBytes12: nadoShared.subaccountNameToBytes12,
  subaccountNameBytesToStr: nadoShared.subaccountNameBytesToStr,
  encodeSignedOrder: nadoShared.encodeSignedOrder,
  encodeSignedWithdrawCollateralTx: nadoShared.encodeSignedWithdrawCollateralTx,
  depositCollateral: nadoShared.depositCollateral,
  approveDepositAllowance: nadoShared.approveDepositAllowance,
  CLEARINGHOUSE_ABI: nadoShared.CLEARINGHOUSE_ABI,
  ENDPOINT_ABI: nadoShared.ENDPOINT_ABI,
  ERC20_ABI: nadoShared.ERC20_ABI,
  SPOT_ENGINE_ABI: nadoShared.SPOT_ENGINE_ABI,
  PERP_ENGINE_ABI: nadoShared.PERP_ENGINE_ABI,
  QUERIER_ABI: nadoShared.QUERIER_ABI,
  WITHDRAW_POOL_ABI: nadoShared.WITHDRAW_POOL_ABI,

  // Interest rate calculations
  calcRealizedDepositRateForTimeRange: nadoShared.calcRealizedDepositRateForTimeRange,
  calcBorrowRateForTimeRange: nadoShared.calcBorrowRateForTimeRange,
  calcBorrowRatePerSecond: nadoShared.calcBorrowRatePerSecond,
  calcUtilizationRatio: nadoShared.calcUtilizationRatio,
  calcTotalBorrowed: nadoShared.calcTotalBorrowed,
  calcTotalDeposited: nadoShared.calcTotalDeposited,

  // Balance calculation functions (used in subaccount hooks and calc utilities)
  calcPerpBalanceNotionalValue: nadoShared.calcPerpBalanceNotionalValue,
  calcPerpBalanceValue: nadoShared.calcPerpBalanceValue,
  calcSpotBalanceValue: nadoShared.calcSpotBalanceValue,
  isPerpBalance: nadoShared.isPerpBalance,
  isSpotBalance: nadoShared.isSpotBalance,
  isPerpProduct: nadoShared.isPerpProduct,
  isSpotProduct: nadoShared.isSpotProduct,

  // LP balance calculation stub (no LP pools in Stackd Perps)
  calcLpBalanceValue: (_balance) => new BigNumber(0),

  // Stubs / compatibility shims
  parseRawExpirationTimestamp,
  getExpirationTimestamp,
  getTriggerOrderNonce,
  OrderExpirationType,
  calcLpTokenValue,

  // Types-only in original SDK — export as undefined stubs
  BalanceSide: { ASSET: 'ASSET', LIABILITY: 'LIABILITY' },
  HealthGroup: { INITIAL: 0, MAINTENANCE: 1 },
  ChainEnv: undefined, // type-only, used as string in runtime

  // Vertex-specific product IDs not in Nado
  VLP_PRODUCT_ID: -1,
};
